import { useState, useEffect, useCallback } from "react";
import {
  imageApi,
  type EntityImage,
  type ImageEntityType,
  type ImageType,
} from "../../services/imageService";
import {
  poolApi,
  technicianApi,
  type Pool as ApiPool,
  type Technician as ApiTechnician,
} from "../../services/poolService";
import {
  tenantAdminApi,
  type TenantAdminCreateUserRequest,
  type TenantAdminUpdateUserRequest,
  type TenantManagedUser,
  type TenantRole,
} from "../../services/adminService";
import { useAuth } from "../../hooks/useAuth";
import { usePermissions } from "../../hooks/usePermissions";
import { ROLES } from "../../types/permissions";

type Pool = ApiPool;

type Technician = ApiTechnician;

interface PoolFormState {
  name: string;
  location: string;
  capacity: string;
  type: string;
}

interface TechnicianFormState {
  name: string;
  email: string;
  phone: string;
  specialty: string;
  active: boolean;
}

interface TenantUserFormState {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  roleId: number;
  technicianId: string;
  isActive: boolean;
}

interface TechAccountFormState {
  username: string;
  password: string;
  roleId: number;
}

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const IMAGE_CONFIG = {
  pool: {
    entityType: "POOL" as ImageEntityType,
    imageType: "POOL_IMAGE" as ImageType,
    emptyMessage: "Esta piscina no tiene imágenes registradas.",
  },
  tech: {
    entityType: "TECHNICIAN" as ImageEntityType,
    imageType: "TECHNICIAN_PROFILE" as ImageType,
    emptyMessage: "Este técnico no tiene imágenes registradas.",
  },
};

const EMPTY_POOL_FORM: PoolFormState = {
  name: "",
  location: "",
  capacity: "",
  type: "commercial",
};

const EMPTY_TECH_FORM: TechnicianFormState = {
  name: "",
  email: "",
  phone: "",
  specialty: "general",
  active: true,
};

const EMPTY_TENANT_USER_FORM: TenantUserFormState = {
  username: "",
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  roleId: ROLES.SUPERVISOR,
  technicianId: "",
  isActive: true,
};

const EMPTY_TECH_ACCOUNT_FORM: TechAccountFormState = {
  username: "",
  password: "",
  roleId: ROLES.TECHNICIAN,
};

const getPrimaryImageUrl = (images: EntityImage[]) => {
  const primaryImage = images.find((image) => image.isPrimary);
  return primaryImage?.url || images[0]?.url || "";
};

const splitFullName = (fullName: string) => {
  const trimmedName = fullName.trim();
  if (!trimmedName) {
    return { firstName: "", lastName: "" };
  }

  const nameParts = trimmedName.split(/\s+/);
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: "" };
  }

  return {
    firstName: nameParts.slice(0, -1).join(" "),
    lastName: nameParts.slice(-1).join(" "),
  };
};

const joinName = (firstName?: string, lastName?: string) =>
  [firstName, lastName].filter(Boolean).join(" ").trim();

const toOptionalNumber = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : undefined;
};

const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "Solo se permiten imágenes JPEG, PNG o WebP.";
  }

  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return "La imagen no puede superar los 5MB.";
  }

  return null;
};

const readPreviewUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new Error("No fue posible leer la imagen seleccionada."));
    reader.readAsDataURL(file);
  });

export default function Configuracion() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"pools" | "technicians" | "users">(
    "pools",
  );
  const [poolsList, setPoolsList] = useState<Pool[]>([]);
  const [techniciansList, setTechniciansList] = useState<Technician[]>([]);
  const [tenantUsers, setTenantUsers] = useState<TenantManagedUser[]>([]);
  const [availableRoles, setAvailableRoles] = useState<TenantRole[]>([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [loadingTechs, setLoadingTechs] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [showTechModal, setShowTechModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingPool, setEditingPool] = useState<Pool | null>(null);
  const [editingTech, setEditingTech] = useState<Technician | null>(null);
  const [editingTenantUser, setEditingTenantUser] =
    useState<TenantManagedUser | null>(null);
  const [savingPool, setSavingPool] = useState(false);
  const [savingTech, setSavingTech] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [poolImages, setPoolImages] = useState<EntityImage[]>([]);
  const [techImages, setTechImages] = useState<EntityImage[]>([]);
  const [loadingPoolImages, setLoadingPoolImages] = useState(false);
  const [loadingTechImages, setLoadingTechImages] = useState(false);
  const [pendingPoolImageFile, setPendingPoolImageFile] = useState<File | null>(
    null,
  );
  const [pendingTechImageFile, setPendingTechImageFile] = useState<File | null>(
    null,
  );
  const [poolImagePreview, setPoolImagePreview] = useState("");
  const [techImagePreview, setTechImagePreview] = useState("");
  const [poolImageError, setPoolImageError] = useState("");
  const [techImageError, setTechImageError] = useState("");
  const [processingPoolImageId, setProcessingPoolImageId] = useState<
    number | null
  >(null);
  const [processingTechImageId, setProcessingTechImageId] = useState<
    number | null
  >(null);
  const [processingUserId, setProcessingUserId] = useState<number | null>(null);
  const [userError, setUserError] = useState("");
  const [userSuccess, setUserSuccess] = useState("");
  const [tenantUserForm, setTenantUserForm] = useState<TenantUserFormState>(
    EMPTY_TENANT_USER_FORM,
  );
  const [techAccountEnabled, setTechAccountEnabled] = useState(false);
  const [techLinkedUserId, setTechLinkedUserId] = useState<number | null>(null);
  const [techLinkedRoleId, setTechLinkedRoleId] = useState<number | null>(null);
  const [techAccountForm, setTechAccountForm] = useState<TechAccountFormState>(
    EMPTY_TECH_ACCOUNT_FORM,
  );

  const {
    canWritePool,
    canDeletePool,
    canWriteTechnician,
    canDeleteTechnician,
  } = usePermissions();

  const [poolForm, setPoolForm] = useState<PoolFormState>(EMPTY_POOL_FORM);

  const [techForm, setTechForm] =
    useState<TechnicianFormState>(EMPTY_TECH_FORM);

  const canManageTenantUsers =
    !!user && !user.superAdmin && user.roleId === ROLES.ADMIN;

  const loadPrimaryImage = useCallback(
    async (
      entityId: number,
      entityType: ImageEntityType,
      fallbackUrl?: string,
    ) => {
      try {
        const image = await imageApi.getPrimaryByEntity(entityId, entityType);
        return image?.url || fallbackUrl || "";
      } catch {
        return fallbackUrl || "";
      }
    },
    [],
  );

  const loadPools = useCallback(async () => {
    try {
      setLoadingPools(true);
      const pools = await poolApi.getAll();
      const activePools = pools.filter((pool) => pool.active);
      const poolsWithImages = await Promise.all(
        activePools.map(async (pool) => ({
          ...pool,
          imageUrl: await loadPrimaryImage(
            pool.id,
            IMAGE_CONFIG.pool.entityType,
            pool.imageUrl,
          ),
        })),
      );
      setPoolsList(poolsWithImages);
    } catch (error) {
      console.error("Error al cargar piscinas:", error);
    } finally {
      setLoadingPools(false);
    }
  }, [loadPrimaryImage]);

  const loadTechnicians = useCallback(async () => {
    try {
      setLoadingTechs(true);
      const technicians = await technicianApi.getAll();
      const activeTechnicians = technicians.filter((tech) => tech.active);
      const techniciansWithImages = await Promise.all(
        activeTechnicians.map(async (tech) => ({
          ...tech,
          imageUrl: await loadPrimaryImage(
            tech.id,
            IMAGE_CONFIG.tech.entityType,
            tech.imageUrl,
          ),
        })),
      );
      setTechniciansList(techniciansWithImages);
    } catch (error) {
      console.error("Error al cargar técnicos:", error);
    } finally {
      setLoadingTechs(false);
    }
  }, [loadPrimaryImage]);

  const loadTenantUsers = useCallback(async () => {
    if (!canManageTenantUsers) {
      setTenantUsers([]);
      return;
    }

    try {
      setLoadingUsers(true);
      const users = await tenantAdminApi.getUsers();
      setTenantUsers(users);
    } catch (error) {
      console.error("Error al cargar usuarios del tenant:", error);
      setUserError(
        error instanceof Error
          ? error.message
          : "Error al cargar los usuarios del tenant.",
      );
    } finally {
      setLoadingUsers(false);
    }
  }, [canManageTenantUsers]);

  const loadAvailableRoles = useCallback(async () => {
    if (!canManageTenantUsers) {
      setAvailableRoles([]);
      return;
    }

    try {
      const roles = await tenantAdminApi.getRoles();
      setAvailableRoles(roles);
    } catch (error) {
      console.error("Error al cargar roles del tenant:", error);
      setUserError(
        error instanceof Error
          ? error.message
          : "Error al cargar los roles disponibles.",
      );
    }
  }, [canManageTenantUsers]);

  // Cargar datos desde las APIs
  useEffect(() => {
    loadPools();
    loadTechnicians();
  }, [loadPools, loadTechnicians]);

  useEffect(() => {
    if (!canManageTenantUsers) {
      setTenantUsers([]);
      setAvailableRoles([]);
      return;
    }

    void loadTenantUsers();
    void loadAvailableRoles();
  }, [canManageTenantUsers, loadTenantUsers, loadAvailableRoles]);

  const getLinkedUserByTechnicianId = useCallback(
    (technicianId: number) =>
      tenantUsers.find(
        (tenantUser) => tenantUser.technicianId === technicianId,
      ),
    [tenantUsers],
  );

  const getRoleLabel = useCallback(
    (roleId: number) => {
      const role = availableRoles.find((item) => item.id === roleId);
      if (role?.description) {
        return `${role.name} · ${role.description}`;
      }
      return role?.name || `Rol ${roleId}`;
    },
    [availableRoles],
  );

  const resetTenantUserForm = useCallback(() => {
    setTenantUserForm(EMPTY_TENANT_USER_FORM);
    setEditingTenantUser(null);
  }, []);

  const resetTechAccessForm = useCallback(() => {
    setTechAccountEnabled(false);
    setTechLinkedUserId(null);
    setTechLinkedRoleId(null);
    setTechAccountForm(EMPTY_TECH_ACCOUNT_FORM);
  }, []);

  const loadEntityImages = async (
    entityId: number,
    entityType: ImageEntityType,
  ) => {
    try {
      return await imageApi.getByEntity(entityId, entityType);
    } catch (error) {
      console.error("Error al cargar imágenes:", error);
      return [];
    }
  };

  const loadPoolImages = async (poolId: number) => {
    try {
      setLoadingPoolImages(true);
      const images = await loadEntityImages(
        poolId,
        IMAGE_CONFIG.pool.entityType,
      );
      setPoolImages(images);
      if (!pendingPoolImageFile) {
        setPoolImagePreview(getPrimaryImageUrl(images));
      }
    } finally {
      setLoadingPoolImages(false);
    }
  };

  const loadTechImages = async (techId: number) => {
    try {
      setLoadingTechImages(true);
      const images = await loadEntityImages(
        techId,
        IMAGE_CONFIG.tech.entityType,
      );
      setTechImages(images);
      if (!pendingTechImageFile) {
        setTechImagePreview(getPrimaryImageUrl(images));
      }
    } finally {
      setLoadingTechImages(false);
    }
  };

  const resetPoolImageState = () => {
    setPoolImages([]);
    setPendingPoolImageFile(null);
    setPoolImagePreview("");
    setPoolImageError("");
    setProcessingPoolImageId(null);
  };

  const resetTechImageState = () => {
    setTechImages([]);
    setPendingTechImageFile(null);
    setTechImagePreview("");
    setTechImageError("");
    setProcessingTechImageId(null);
  };

  const openCreateTenantUserModal = () => {
    resetTenantUserForm();
    setUserError("");
    setUserSuccess("");
    setShowUserModal(true);
  };

  const openEditTenantUserModal = (tenantUser: TenantManagedUser) => {
    setEditingTenantUser(tenantUser);
    setTenantUserForm({
      username: tenantUser.username,
      email: tenantUser.email,
      firstName: tenantUser.firstName || "",
      lastName: tenantUser.lastName || "",
      password: "",
      roleId: tenantUser.roleIds[0] || ROLES.SUPERVISOR,
      technicianId: tenantUser.technicianId
        ? String(tenantUser.technicianId)
        : "",
      isActive: tenantUser.isActive,
    });
    setUserError("");
    setUserSuccess("");
    setShowUserModal(true);
  };

  const handleSaveTenantUser = async () => {
    if (
      !tenantUserForm.username.trim() ||
      !tenantUserForm.email.trim() ||
      !tenantUserForm.firstName.trim() ||
      !tenantUserForm.lastName.trim()
    ) {
      setUserError("Completa usuario, email, nombre y apellido.");
      return;
    }

    if (!editingTenantUser && !tenantUserForm.password.trim()) {
      setUserError("La contraseña es obligatoria al crear un usuario.");
      return;
    }

    try {
      setSavingUser(true);
      setUserError("");

      const technicianId = toOptionalNumber(tenantUserForm.technicianId);

      if (editingTenantUser) {
        const payload: TenantAdminUpdateUserRequest = {
          username: tenantUserForm.username.trim(),
          email: tenantUserForm.email.trim(),
          firstName: tenantUserForm.firstName.trim(),
          lastName: tenantUserForm.lastName.trim(),
          isActive: tenantUserForm.isActive,
          technicianId: technicianId ?? null,
          ...(tenantUserForm.password.trim()
            ? { password: tenantUserForm.password.trim() }
            : {}),
        };

        await tenantAdminApi.updateUser(editingTenantUser.id, payload);

        const currentRoleId = editingTenantUser.roleIds[0];
        if (currentRoleId !== tenantUserForm.roleId) {
          await tenantAdminApi.replaceUserRole(
            editingTenantUser.id,
            tenantUserForm.roleId,
          );
        }

        setUserSuccess(
          `Usuario ${tenantUserForm.email} actualizado correctamente.`,
        );
      } else {
        const payload: TenantAdminCreateUserRequest = {
          username: tenantUserForm.username.trim(),
          email: tenantUserForm.email.trim(),
          password: tenantUserForm.password.trim(),
          firstName: tenantUserForm.firstName.trim(),
          lastName: tenantUserForm.lastName.trim(),
          roleId: tenantUserForm.roleId,
          ...(technicianId ? { technicianId } : {}),
        };

        await tenantAdminApi.createUser(payload);
        setUserSuccess(`Usuario ${tenantUserForm.email} creado correctamente.`);
      }

      setShowUserModal(false);
      resetTenantUserForm();
      await loadTenantUsers();
    } catch (error) {
      console.error("Error al guardar usuario del tenant:", error);
      setUserError(
        error instanceof Error ? error.message : "Error al guardar el usuario.",
      );
    } finally {
      setSavingUser(false);
    }
  };

  const handleToggleTenantUserStatus = async (
    tenantUser: TenantManagedUser,
  ) => {
    try {
      setProcessingUserId(tenantUser.id);
      setUserError("");

      if (tenantUser.isActive) {
        await tenantAdminApi.deactivateUser(tenantUser.id);
        setUserSuccess(`Usuario ${tenantUser.email} desactivado.`);
      } else {
        await tenantAdminApi.activateUser(tenantUser.id);
        setUserSuccess(`Usuario ${tenantUser.email} activado.`);
      }

      await loadTenantUsers();
    } catch (error) {
      console.error("Error al cambiar estado del usuario:", error);
      setUserError(
        error instanceof Error ? error.message : "Error al cambiar el estado.",
      );
    } finally {
      setProcessingUserId(null);
    }
  };

  // Pool Functions
  const handleAddPool = () => {
    setEditingPool(null);
    setPoolForm(EMPTY_POOL_FORM);
    resetPoolImageState();
    setShowPoolModal(true);
  };

  const handleEditPool = (pool: Pool) => {
    setEditingPool(pool);
    setPoolForm({
      name: pool.name,
      location: pool.location,
      capacity: pool.capacity,
      type: pool.type,
    });
    resetPoolImageState();
    setPoolImagePreview(pool.imageUrl || "");
    setShowPoolModal(true);
    void loadPoolImages(Number(pool.id));
  };

  const handleDeletePool = async (id: number | string) => {
    if (confirm("¿Estás seguro de eliminar esta piscina?")) {
      try {
        await poolApi.delete(Number(id));
        await loadPools(); // Recargar la lista
        alert("Piscina eliminada exitosamente");
      } catch (error) {
        console.error("Error al eliminar piscina:", error);
        alert("Error al eliminar la piscina");
      }
    }
  };

  const handleSavePool = async () => {
    if (!poolForm.name || !poolForm.location || !poolForm.capacity) {
      alert("Por favor completa todos los campos obligatorios");
      return;
    }

    try {
      setSavingPool(true);
      const poolData = {
        name: poolForm.name,
        location: poolForm.location,
        capacity: poolForm.capacity,
        type: poolForm.type,
      };

      const hasPrimaryImage = poolImages.some((image) => image.isPrimary);
      let savedPool: Pool;

      if (editingPool) {
        savedPool = await poolApi.update(Number(editingPool.id), poolData);
        alert("Piscina actualizada exitosamente");
      } else {
        savedPool = await poolApi.create(poolData);
        alert("Piscina creada exitosamente");
      }

      if (pendingPoolImageFile) {
        const uploadedImage = await imageApi.upload({
          file: pendingPoolImageFile,
          entityId: Number(savedPool.id),
          entityType: IMAGE_CONFIG.pool.entityType,
          imageType: IMAGE_CONFIG.pool.imageType,
        });

        if (!hasPrimaryImage) {
          await imageApi.setPrimary(uploadedImage.id);
        }
      }

      setShowPoolModal(false);
      resetPoolImageState();
      await loadPools(); // Recargar la lista
    } catch (error) {
      console.error("Error al guardar piscina:", error);
      alert("Error al guardar la piscina");
    } finally {
      setSavingPool(false);
    }
  };

  // Technician Functions
  const handleAddTech = () => {
    setEditingTech(null);
    setTechForm(EMPTY_TECH_FORM);
    resetTechImageState();
    resetTechAccessForm();
    setShowTechModal(true);
  };

  const handleEditTech = (tech: Technician) => {
    const linkedUser = getLinkedUserByTechnicianId(Number(tech.id));
    const { firstName, lastName } = splitFullName(tech.name);

    setEditingTech(tech);
    setTechForm({
      name: tech.name,
      email: tech.email,
      phone: tech.phone || "",
      specialty: tech.specialty,
      active: tech.active,
    });
    resetTechImageState();
    if (linkedUser) {
      setTechAccountEnabled(true);
      setTechLinkedUserId(linkedUser.id);
      setTechLinkedRoleId(linkedUser.roleIds[0] || ROLES.TECHNICIAN);
      setTechAccountForm({
        username: linkedUser.username,
        password: "",
        roleId: linkedUser.roleIds[0] || ROLES.TECHNICIAN,
      });
    } else {
      setTechAccountEnabled(false);
      setTechLinkedUserId(null);
      setTechLinkedRoleId(null);
      setTechAccountForm({
        username:
          tech.email.split("@")[0] ||
          `${firstName}.${lastName}`.replace(/\s+/g, ".").toLowerCase(),
        password: "",
        roleId: ROLES.TECHNICIAN,
      });
    }
    setTechImagePreview(tech.imageUrl || "");
    setShowTechModal(true);
    void loadTechImages(Number(tech.id));
  };

  const handleDeleteTech = async (id: number | string) => {
    if (confirm("¿Estás seguro de eliminar este técnico?")) {
      try {
        await technicianApi.delete(Number(id));
        await loadTechnicians(); // Recargar la lista
        alert("Técnico eliminado exitosamente");
      } catch (error) {
        console.error("Error al eliminar técnico:", error);
        alert("Error al eliminar el técnico");
      }
    }
  };

  const handleSaveTech = async () => {
    if (!techForm.name || !techForm.email || !techForm.phone) {
      alert("Por favor completa todos los campos obligatorios");
      return;
    }

    if (canManageTenantUsers && techAccountEnabled) {
      if (!techAccountForm.username.trim()) {
        alert("Debes indicar el nombre de usuario para el acceso del técnico.");
        return;
      }

      if (!techLinkedUserId && !techAccountForm.password.trim()) {
        alert("Debes asignar una contraseña al crear el acceso del técnico.");
        return;
      }
    }

    try {
      setSavingTech(true);
      const techData = {
        name: techForm.name,
        email: techForm.email,
        phone: techForm.phone,
        specialty: techForm.specialty,
        active: techForm.active,
      };

      const hasPrimaryImage = techImages.some((image) => image.isPrimary);
      let savedTech: Technician;

      if (editingTech) {
        savedTech = await technicianApi.update(
          Number(editingTech.id),
          techData,
        );
        alert("Técnico actualizado exitosamente");
      } else {
        savedTech = await technicianApi.create(techData);
        alert("Técnico creado exitosamente");
      }

      if (pendingTechImageFile) {
        const uploadedImage = await imageApi.upload({
          file: pendingTechImageFile,
          entityId: Number(savedTech.id),
          entityType: IMAGE_CONFIG.tech.entityType,
          imageType: IMAGE_CONFIG.tech.imageType,
        });

        if (!hasPrimaryImage) {
          await imageApi.setPrimary(uploadedImage.id);
        }
      }

      if (canManageTenantUsers && techAccountEnabled) {
        const { firstName, lastName } = splitFullName(techForm.name);

        if (techLinkedUserId) {
          const payload: TenantAdminUpdateUserRequest = {
            username: techAccountForm.username.trim(),
            email: techForm.email.trim(),
            firstName,
            lastName,
            technicianId: Number(savedTech.id),
            isActive: techForm.active,
            ...(techAccountForm.password.trim()
              ? { password: techAccountForm.password.trim() }
              : {}),
          };

          await tenantAdminApi.updateUser(techLinkedUserId, payload);

          if (techLinkedRoleId !== techAccountForm.roleId) {
            await tenantAdminApi.replaceUserRole(
              techLinkedUserId,
              techAccountForm.roleId,
            );
          }
        } else {
          await tenantAdminApi.createUser({
            username: techAccountForm.username.trim(),
            email: techForm.email.trim(),
            password: techAccountForm.password.trim(),
            firstName,
            lastName,
            roleId: techAccountForm.roleId,
            technicianId: Number(savedTech.id),
          });
        }

        await loadTenantUsers();
      }

      setShowTechModal(false);
      resetTechImageState();
      resetTechAccessForm();
      await loadTechnicians(); // Recargar la lista
    } catch (error) {
      console.error("Error al guardar técnico:", error);
      alert("Error al guardar el técnico");
    } finally {
      setSavingTech(false);
    }
  };

  const handleImageSelection = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "pool" | "tech",
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const validationError = validateImageFile(file);

        if (validationError) {
          if (type === "pool") {
            setPoolImageError(validationError);
          } else {
            setTechImageError(validationError);
          }
          return;
        }

        const previewUrl = await readPreviewUrl(file);

        if (type === "pool") {
          setPoolImageError("");
          setPendingPoolImageFile(file);
          setPoolImagePreview(previewUrl);
        } else {
          setTechImageError("");
          setPendingTechImageFile(file);
          setTechImagePreview(previewUrl);
        }
      } catch (error) {
        console.error("Error al subir imagen:", error);
        if (type === "pool") {
          setPoolImageError("Error al procesar la imagen seleccionada.");
          setPendingPoolImageFile(null);
          setPoolImagePreview("");
        } else {
          setTechImageError("Error al procesar la imagen seleccionada.");
          setPendingTechImageFile(null);
          setTechImagePreview("");
        }
      }

      e.target.value = "";
    }
  };

  const handleSetPrimaryImage = async (
    imageId: number,
    type: "pool" | "tech",
  ) => {
    try {
      if (type === "pool" && editingPool) {
        setProcessingPoolImageId(imageId);
        await imageApi.setPrimary(imageId);
        await loadPoolImages(Number(editingPool.id));
        await loadPools();
      }

      if (type === "tech" && editingTech) {
        setProcessingTechImageId(imageId);
        await imageApi.setPrimary(imageId);
        await loadTechImages(Number(editingTech.id));
        await loadTechnicians();
      }
    } catch (error) {
      console.error("Error al marcar imagen principal:", error);
      alert("Error al marcar la imagen como principal.");
    } finally {
      if (type === "pool") {
        setProcessingPoolImageId(null);
      } else {
        setProcessingTechImageId(null);
      }
    }
  };

  const handleDeleteImage = async (imageId: number, type: "pool" | "tech") => {
    if (!confirm("¿Estás seguro de eliminar esta imagen?")) {
      return;
    }

    try {
      if (type === "pool" && editingPool) {
        setProcessingPoolImageId(imageId);
        await imageApi.delete(imageId);
        await loadPoolImages(Number(editingPool.id));
        await loadPools();
      }

      if (type === "tech" && editingTech) {
        setProcessingTechImageId(imageId);
        await imageApi.delete(imageId);
        await loadTechImages(Number(editingTech.id));
        await loadTechnicians();
      }
    } catch (error) {
      console.error("Error al eliminar imagen:", error);
      alert("Error al eliminar la imagen.");
    } finally {
      if (type === "pool") {
        setProcessingPoolImageId(null);
      } else {
        setProcessingTechImageId(null);
      }
    }
  };

  const renderImageGallery = (
    images: EntityImage[],
    type: "pool" | "tech",
    isLoading: boolean,
    entityExists: boolean,
    processingImageId: number | null,
  ) => {
    const emptyMessage =
      type === "pool"
        ? IMAGE_CONFIG.pool.emptyMessage
        : IMAGE_CONFIG.tech.emptyMessage;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-semibold text-slate-700">
            Imágenes registradas
          </label>
          {!entityExists && (
            <span className="text-xs text-slate-500">
              Guarda la entidad para registrar imágenes en backend.
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : images.length === 0 ? (
          <p className="text-sm text-slate-500">{emptyMessage}</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {images.map((image) => (
              <div
                key={image.id}
                className="rounded-2xl border border-slate-200 p-4"
              >
                <div className="mb-3 h-36 overflow-hidden rounded-xl bg-slate-100">
                  <img
                    src={image.url}
                    alt={image.fileName}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="mb-3">
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {image.fileName}
                  </p>
                  <p className="text-xs text-slate-500">
                    {image.mimeType} · {Math.round(image.fileSize / 1024)} KB
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={image.isPrimary || processingImageId === image.id}
                    onClick={() => handleSetPrimaryImage(image.id, type)}
                    className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-all ${image.isPrimary ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"}`}
                  >
                    {image.isPrimary ? "Principal" : "Marcar principal"}
                  </button>
                  <button
                    type="button"
                    disabled={processingImageId === image.id}
                    onClick={() => handleDeleteImage(image.id, type)}
                    className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600 transition-all hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section
      id="configuracion"
      className="py-20 bg-gradient-to-br from-slate-50 to-gray-100 min-h-screen"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">
            Configuración
          </h2>
          <p className="text-xl text-slate-600">
            Gestiona piscinas, técnicos y accesos operativos del tenant
          </p>
        </div>

        {(userSuccess || userError) && (
          <div
            className={`mb-6 rounded-2xl border px-5 py-4 text-sm font-medium ${userSuccess ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`}
          >
            <div className="flex items-center gap-3">
              <i
                className={`${userSuccess ? "ri-check-double-line" : "ri-error-warning-line"} text-lg`}
              />
              <span>{userSuccess || userError}</span>
              <button
                type="button"
                onClick={() => {
                  setUserSuccess("");
                  setUserError("");
                }}
                className="ml-auto"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-full p-1 shadow-lg inline-flex">
            <button
              onClick={() => setActiveTab("pools")}
              className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === "pools"
                  ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-white"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              <i className="ri-water-flash-line mr-2"></i>
              Piscinas
            </button>
            <button
              onClick={() => setActiveTab("technicians")}
              className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === "technicians"
                  ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-white"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              <i className="ri-user-settings-line mr-2"></i>
              Técnicos
            </button>
            {canManageTenantUsers && (
              <button
                onClick={() => setActiveTab("users")}
                className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === "users"
                    ? "bg-gradient-to-r from-teal-500 to-cyan-600 text-white"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <i className="ri-admin-line mr-2"></i>
                Usuarios
              </button>
            )}
          </div>
        </div>

        {/* Pools Tab */}
        {activeTab === "pools" && (
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-slate-800">
                <i className="ri-water-flash-line text-teal-600 mr-2"></i>
                Gestión de Piscinas
              </h3>
              {canWritePool && (
                <button
                  onClick={handleAddPool}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  Agregar Piscina
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loadingPools ? (
                <div className="col-span-3 flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                </div>
              ) : (
                poolsList.map((pool) => (
                  <div
                    key={pool.id}
                    className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
                  >
                    {pool.imageUrl && (
                      <div className="w-full h-48 mb-4 rounded-xl overflow-hidden">
                        <img
                          src={pool.imageUrl}
                          alt={pool.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <h4 className="text-xl font-bold text-slate-800 mb-2">
                      {pool.name}
                    </h4>
                    <div className="space-y-2 mb-4">
                      <p className="text-sm text-slate-600">
                        <i className="ri-map-pin-line mr-2 text-teal-600"></i>
                        {pool.location}
                      </p>
                      <p className="text-sm text-slate-600">
                        <i className="ri-drop-line mr-2 text-teal-600"></i>
                        Capacidad: {pool.capacity}
                      </p>
                      <p className="text-sm text-slate-600">
                        <i className="ri-building-line mr-2 text-teal-600"></i>
                        Tipo:{" "}
                        {pool.type === "residential"
                          ? "Residencial"
                          : pool.type === "commercial"
                            ? "Comercial"
                            : pool.type === "olympic"
                              ? "Olímpica"
                              : pool.type === "therapeutic"
                                ? "Terapéutica"
                                : pool.type}
                      </p>
                    </div>
                    {(canWritePool || canDeletePool) && (
                      <div className="flex space-x-2">
                        {canWritePool && (
                          <button
                            onClick={() => handleEditPool(pool)}
                            className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-all whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-edit-line mr-1"></i>
                            Editar
                          </button>
                        )}
                        {canDeletePool && (
                          <button
                            onClick={() => handleDeletePool(pool.id)}
                            className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-all whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>
                            Eliminar
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Technicians Tab */}
        {activeTab === "technicians" && (
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-slate-800">
                <i className="ri-user-settings-line text-teal-600 mr-2"></i>
                Gestión de Técnicos
              </h3>
              {canWriteTechnician && (
                <button
                  onClick={handleAddTech}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  Agregar Técnico
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loadingTechs ? (
                <div className="col-span-3 flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                </div>
              ) : (
                techniciansList.map((tech) => (
                  <div
                    key={tech.id}
                    className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-20 h-20 rounded-full overflow-hidden bg-teal-100 flex items-center justify-center">
                        {tech.imageUrl ? (
                          <img
                            src={tech.imageUrl}
                            alt={tech.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <i className="ri-user-line text-4xl text-teal-600"></i>
                        )}
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          tech.active
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {tech.active ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                    <h4 className="text-xl font-bold text-slate-800 mb-2">
                      {tech.name}
                    </h4>
                    <div className="space-y-2 mb-4">
                      <p className="text-sm text-slate-600">
                        <i className="ri-mail-line mr-2 text-teal-600"></i>
                        {tech.email}
                      </p>
                      <p className="text-sm text-slate-600">
                        <i className="ri-phone-line mr-2 text-teal-600"></i>
                        {tech.phone}
                      </p>
                      <p className="text-sm text-slate-600">
                        <i className="ri-award-line mr-2 text-teal-600"></i>
                        {tech.specialty === "general"
                          ? "General"
                          : tech.specialty === "chemical"
                            ? "Químico"
                            : "Mantenimiento"}
                      </p>
                    </div>
                    {(canWriteTechnician || canDeleteTechnician) && (
                      <div className="flex space-x-2">
                        {canWriteTechnician && (
                          <button
                            onClick={() => handleEditTech(tech)}
                            className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-all whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-edit-line mr-1"></i>
                            Editar
                          </button>
                        )}
                        {canDeleteTechnician && (
                          <button
                            onClick={() => handleDeleteTech(tech.id)}
                            className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-all whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-delete-bin-line mr-1"></i>
                            Eliminar
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === "users" && canManageTenantUsers && (
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-slate-800">
                  <i className="ri-admin-line text-teal-600 mr-2"></i>
                  Usuarios del Tenant
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Crea supervisores, visualizadores o accesos ligados a
                  técnicos.
                </p>
              </div>
              <button
                onClick={openCreateTenantUserModal}
                className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-line mr-2"></i>
                Nuevo Usuario
              </button>
            </div>

            {loadingUsers ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              </div>
            ) : tenantUsers.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500">
                No hay usuarios creados todavía en este tenant.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {tenantUsers.map((tenantUser) => (
                  <div
                    key={tenantUser.id}
                    className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <h4 className="text-lg font-bold text-slate-800">
                          {joinName(
                            tenantUser.firstName,
                            tenantUser.lastName,
                          ) || tenantUser.username}
                        </h4>
                        <p className="text-sm text-slate-500">
                          @{tenantUser.username}
                        </p>
                      </div>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${tenantUser.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                      >
                        {tenantUser.isActive ? "Activo" : "Inactivo"}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm text-slate-600 mb-5">
                      <p>
                        <i className="ri-mail-line mr-2 text-teal-600"></i>
                        {tenantUser.email}
                      </p>
                      <p>
                        <i className="ri-shield-user-line mr-2 text-teal-600"></i>
                        {tenantUser.roleNames[0] ||
                          getRoleLabel(tenantUser.roleIds[0] || 0)}
                      </p>
                      <p>
                        <i className="ri-links-line mr-2 text-teal-600"></i>
                        {tenantUser.technicianId
                          ? `Vinculado a técnico #${tenantUser.technicianId}`
                          : "Sin vínculo a técnico"}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditTenantUserModal(tenantUser)}
                        className="flex-1 rounded-xl bg-blue-500 px-4 py-2.5 text-white font-semibold hover:bg-blue-600 transition-all"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleToggleTenantUserStatus(tenantUser)}
                        disabled={processingUserId === tenantUser.id}
                        className={`flex-1 rounded-xl px-4 py-2.5 font-semibold transition-all disabled:opacity-70 ${tenantUser.isActive ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}
                      >
                        {processingUserId === tenantUser.id
                          ? "Procesando..."
                          : tenantUser.isActive
                            ? "Desactivar"
                            : "Activar"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pool Modal */}
        {showPoolModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">
                {editingPool ? "Editar Piscina" : "Agregar Nueva Piscina"}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={poolForm.name}
                    onChange={(e) =>
                      setPoolForm({ ...poolForm, name: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="Ej: Piscina Principal"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Ubicación *
                  </label>
                  <input
                    type="text"
                    value={poolForm.location}
                    onChange={(e) =>
                      setPoolForm({ ...poolForm, location: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="Ej: Edificio A, Piso 3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Capacidad *
                  </label>
                  <input
                    type="text"
                    value={poolForm.capacity}
                    onChange={(e) =>
                      setPoolForm({ ...poolForm, capacity: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="Ej: 50,000 litros"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Tipo
                  </label>
                  <select
                    value={poolForm.type}
                    onChange={(e) =>
                      setPoolForm({ ...poolForm, type: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  >
                    <option value="commercial">Comercial</option>
                    <option value="residential">Residencial</option>
                    <option value="olympic">Olímpica</option>
                    <option value="therapeutic">Terapéutica</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Imagen de la Piscina
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageSelection(e, "pool")}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Formatos permitidos: JPEG, PNG o WebP. Tamaño máximo: 5MB.
                    {editingPool
                      ? " Si seleccionas una nueva imagen, se subirá al guardar."
                      : " La imagen se subirá después de crear la piscina."}
                  </p>
                  {poolImageError && (
                    <p className="mt-2 text-sm text-red-600">
                      {poolImageError}
                    </p>
                  )}
                  {poolImagePreview && (
                    <div className="mt-4 w-full h-48 rounded-lg overflow-hidden">
                      <img
                        src={poolImagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>

                {renderImageGallery(
                  poolImages,
                  "pool",
                  loadingPoolImages,
                  !!editingPool,
                  processingPoolImageId,
                )}
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowPoolModal(false);
                    resetPoolImageState();
                  }}
                  className="flex-1 bg-gray-200 text-slate-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-300 transition-all whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSavePool}
                  disabled={savingPool}
                  className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all whitespace-nowrap cursor-pointer disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {savingPool ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Technician Modal */}
        {showTechModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-slate-800 mb-6">
                {editingTech ? "Editar Técnico" : "Agregar Nuevo Técnico"}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    value={techForm.name}
                    onChange={(e) =>
                      setTechForm({ ...techForm, name: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="Ej: Juan Pérez"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={techForm.email}
                    onChange={(e) =>
                      setTechForm({ ...techForm, email: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="Ej: juan@ejemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    value={techForm.phone}
                    onChange={(e) =>
                      setTechForm({ ...techForm, phone: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="Ej: +57 300 123 4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Especialidad
                  </label>
                  <select
                    value={techForm.specialty}
                    onChange={(e) =>
                      setTechForm({ ...techForm, specialty: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  >
                    <option value="general">General</option>
                    <option value="chemical">Químico</option>
                    <option value="maintenance">Mantenimiento</option>
                  </select>
                </div>

                <div>
                  {canManageTenantUsers && (
                    <div className="rounded-2xl border border-cyan-100 bg-cyan-50/70 p-5 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="text-base font-bold text-slate-800">
                            Acceso al sistema
                          </h4>
                          <p className="text-sm text-slate-500">
                            {techLinkedUserId
                              ? "Este técnico ya tiene una cuenta asociada. Puedes actualizar sus credenciales o rol."
                              : "Crea el usuario autenticable del técnico al mismo tiempo."}
                          </p>
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            checked={techAccountEnabled}
                            onChange={(e) =>
                              setTechAccountEnabled(e.target.checked)
                            }
                            className="h-4 w-4 rounded border-slate-300 text-teal-600"
                          />
                          Habilitar acceso
                        </label>
                      </div>

                      {techAccountEnabled && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              Usuario de acceso *
                            </label>
                            <input
                              type="text"
                              value={techAccountForm.username}
                              onChange={(e) =>
                                setTechAccountForm((current) => ({
                                  ...current,
                                  username: e.target.value,
                                }))
                              }
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                              placeholder="Ej: juan.perez"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              Rol de acceso *
                            </label>
                            <select
                              value={techAccountForm.roleId}
                              onChange={(e) =>
                                setTechAccountForm((current) => ({
                                  ...current,
                                  roleId: Number(e.target.value),
                                }))
                              }
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                            >
                              {availableRoles.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {getRoleLabel(role.id)}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              {techLinkedUserId
                                ? "Nueva contraseña"
                                : "Contraseña inicial *"}
                            </label>
                            <input
                              type="password"
                              value={techAccountForm.password}
                              onChange={(e) =>
                                setTechAccountForm((current) => ({
                                  ...current,
                                  password: e.target.value,
                                }))
                              }
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                              placeholder={
                                techLinkedUserId
                                  ? "Déjalo vacío para conservar la actual"
                                  : "Mínimo 6 caracteres"
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Foto del Técnico
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageSelection(e, "tech")}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Formatos permitidos: JPEG, PNG o WebP. Tamaño máximo: 5MB.
                    {editingTech
                      ? " Si seleccionas una nueva imagen, se subirá al guardar."
                      : " La imagen se subirá después de crear el técnico."}
                  </p>
                  {techImageError && (
                    <p className="mt-2 text-sm text-red-600">
                      {techImageError}
                    </p>
                  )}
                  {techImagePreview && (
                    <div className="mt-4 w-32 h-32 rounded-full overflow-hidden mx-auto">
                      <img
                        src={techImagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>

                {renderImageGallery(
                  techImages,
                  "tech",
                  loadingTechImages,
                  !!editingTech,
                  processingTechImageId,
                )}

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="active"
                    checked={techForm.active}
                    onChange={(e) =>
                      setTechForm({ ...techForm, active: e.target.checked })
                    }
                    className="w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  />
                  <label
                    htmlFor="active"
                    className="text-sm font-semibold text-slate-700 cursor-pointer"
                  >
                    Técnico Activo
                  </label>
                </div>
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowTechModal(false);
                    resetTechImageState();
                    resetTechAccessForm();
                  }}
                  className="flex-1 bg-gray-200 text-slate-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-300 transition-all whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveTech}
                  disabled={savingTech}
                  className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all whitespace-nowrap cursor-pointer disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {savingTech ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showUserModal && canManageTenantUsers && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl rounded-3xl bg-white p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-slate-800 mb-2">
                {editingTenantUser
                  ? "Editar Usuario"
                  : "Nuevo Usuario del Tenant"}
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                Crea accesos para personal operativo y vincúlalos opcionalmente
                con un técnico.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={tenantUserForm.firstName}
                    onChange={(e) =>
                      setTenantUserForm((current) => ({
                        ...current,
                        firstName: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Apellido *
                  </label>
                  <input
                    type="text"
                    value={tenantUserForm.lastName}
                    onChange={(e) =>
                      setTenantUserForm((current) => ({
                        ...current,
                        lastName: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Usuario *
                  </label>
                  <input
                    type="text"
                    value={tenantUserForm.username}
                    onChange={(e) =>
                      setTenantUserForm((current) => ({
                        ...current,
                        username: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={tenantUserForm.email}
                    onChange={(e) =>
                      setTenantUserForm((current) => ({
                        ...current,
                        email: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Rol *
                  </label>
                  <select
                    value={tenantUserForm.roleId}
                    onChange={(e) =>
                      setTenantUserForm((current) => ({
                        ...current,
                        roleId: Number(e.target.value),
                      }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  >
                    {availableRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {getRoleLabel(role.id)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Vincular técnico
                  </label>
                  <select
                    value={tenantUserForm.technicianId}
                    onChange={(e) =>
                      setTenantUserForm((current) => ({
                        ...current,
                        technicianId: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  >
                    <option value="">Sin vínculo</option>
                    {techniciansList
                      .filter((tech) => {
                        const linkedUser = getLinkedUserByTechnicianId(tech.id);
                        return (
                          !linkedUser || linkedUser.id === editingTenantUser?.id
                        );
                      })
                      .map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    {editingTenantUser
                      ? "Nueva contraseña"
                      : "Contraseña inicial *"}
                  </label>
                  <input
                    type="password"
                    value={tenantUserForm.password}
                    onChange={(e) =>
                      setTenantUserForm((current) => ({
                        ...current,
                        password: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder={
                      editingTenantUser
                        ? "Déjala vacía para mantener la actual"
                        : "Mínimo 6 caracteres"
                    }
                  />
                </div>

                <div className="md:col-span-2 flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                  <input
                    type="checkbox"
                    id="tenant-user-active"
                    checked={tenantUserForm.isActive}
                    onChange={(e) =>
                      setTenantUserForm((current) => ({
                        ...current,
                        isActive: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-slate-300 text-teal-600"
                  />
                  <label
                    htmlFor="tenant-user-active"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Usuario activo
                  </label>
                </div>
              </div>

              <div className="mt-8 flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUserModal(false);
                    resetTenantUserForm();
                  }}
                  className="flex-1 rounded-xl bg-gray-200 px-6 py-3 font-semibold text-slate-700 hover:bg-gray-300 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveTenantUser}
                  disabled={savingUser}
                  className="flex-1 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-3 font-semibold text-white hover:from-teal-600 hover:to-cyan-700 transition-all disabled:opacity-70"
                >
                  {savingUser ? "Guardando..." : "Guardar usuario"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
